import { b2Vec2 } from "./box2d-compat.js";
import { FlashLevelBaseRuntime } from "./flash-level-base-runtime.js";

const BOX_SYMBOL = Object.freeze({
  imagePath: "port/generated/symbols/271/png/1.png",
  width: 81.5,
  height: 74.2,
  offsetX: -42.2,
  offsetY: -39.75,
});

const LEVEL7_DYNAMIC_SYMBOLS = Object.freeze({
  jumpPlatform1: {
    imagePath: "port/generated/symbols/256/png/1.png",
    width: 428.1,
    height: 309.15,
    offsetX: -173.1,
    offsetY: -151.85,
  },
  movePlatform1: {
    imagePath: "port/generated/symbols/265/png/1.png",
    width: 202.25,
    height: 153.1,
    offsetX: -100.85,
    offsetY: -79.55,
  },
  movePlatform2: {
    imagePath: "port/generated/symbols/265/png/1.png",
    width: 202.25,
    height: 153.1,
    offsetX: -100.85,
    offsetY: -79.55,
  },
  jumpPlatform2: {
    imagePath: "port/generated/symbols/256/png/1.png",
    width: 428.1,
    height: 309.15,
    offsetX: -173.1,
    offsetY: -151.85,
  },
  bigMovePlatform: {
    imagePath: "port/generated/symbols/265/png/1.png",
    width: 202.25,
    height: 153.1,
    offsetX: -100.85,
    offsetY: -79.55,
  },
});

const LEVEL7_DYNAMIC_DRAW_ORDER = Object.freeze([
  "jumpPlatform1",
  "movePlatform1",
  "movePlatform2",
  "jumpPlatform2",
  "bigMovePlatform",
]);

const GOAL_SYMBOL = Object.freeze({
  imageWidth: 25.2,
  imageHeight: 46.5,
  imageOffsetX: -1.0,
  imageOffsetY: -15.2,
});

const CHECKPOINT_SYMBOL = Object.freeze({
  imageWidth: 10.8,
  imageHeight: 21.1,
  imageOffsetX: -0.5,
  imageOffsetY: -0.5,
});

const LEVEL7_BODY_LAYOUT_FIX = Object.freeze({
  firstPlatform: { x: -213.9, y: 260.0 },
  bigPlatform: { x: 658.75, y: 78.45 },
  potolok: { x: 592.95, y: -185.0 },
  movePlatform1: { x: 1324.0, y: 122.0 },
  movePlatform2: { x: 1646.0, y: -31.05 },
  redWall: { x: 2042.45, y: -234.5 },
  endPlatform: { x: 3156.2, y: -183.05 },
  triangle: { x: 1293.9, y: -206.95 },
  vanna: { x: 2322.35, y: -83.45 },
  bigMovePlatform: { x: 3008.85, y: -184.0 },
  bluePlatform: { x: 2294.9, y: -175.0 },
});

function drawSymbolImage(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(image, symbol.offsetX, symbol.offsetY, symbol.width, symbol.height);
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbol, x, y) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.drawImage(image, symbol.imageOffsetX, symbol.imageOffsetY, symbol.imageWidth, symbol.imageHeight);
  ctx.restore();
}

export class LevelSevenRuntime extends FlashLevelBaseRuntime {
  buildLevel() {
    if (this.assetsEnabled) {
      this.stageImage.src = "port/generated/levels_fg/level_7/1.visual.runtime.svg";
      this.boxImage = new Image();
      this.boxImage.src = BOX_SYMBOL.imagePath;
      this.level7DynamicImages = Object.fromEntries(
        Object.entries(LEVEL7_DYNAMIC_SYMBOLS).map(([name, symbol]) => {
          const image = new Image();
          image.src = symbol.imagePath;
          return [name, image];
        }),
      );
    } else {
      this.boxImage = null;
      this.level7DynamicImages = {};
    }

    // Generated level layout can regress several body anchors to (0,0).
    // Restore original timeline positions so physics/collisions stay correct.
    for (const [name, fix] of Object.entries(LEVEL7_BODY_LAYOUT_FIX)) {
      const layout = this.levelData.layout.namedChildren[name];
      if (layout) {
        layout.x = fix.x;
        layout.y = fix.y;
      }
    }

    this.createBody("firstPlatform", "Polygon", 0, 1, 0.2, [[[150, 0], [150, 20], [0, 20], [0, 0]]]);
    this.jumpPlatform1Body = this.createBody("jumpPlatform1", "Polygon", 1, 1, 0.2, [[[100, 0], [100, 20], [0, 20], [0, 0]]]);
    this.createBody("bigPlatform", "Polygon", 0, 1, 0.2, [[[228.2, -82.45], [228.2, -59.45], [-264.8, -47.45], [-264.8, -82.45]], [[-264.8, -47.45], [228.2, -59.45], [-339.8, 11.55]], [[-441.75, 11.55], [-339.8, 11.55], [-527.75, 61.5]], [[-339.8, 11.55], [228.2, -59.45], [254.2, -59.45], [254.2, -36.45], [-527.75, 82.5], [-527.75, 61.5]], [[527.75, 29], [527.75, 82.5], [512.1, 42.5]], [[512.1, 42.5], [527.75, 82.5], [492.9, 51.2]], [[492.9, 51.2], [527.75, 82.5], [475.55, 53.2]], [[527.75, 82.5], [-527.75, 82.5], [457.5, 51.7], [475.55, 53.2]], [[457.5, 51.7], [-527.75, 82.5], [415.55, 41.25]], [[415.55, 41.25], [-527.75, 82.5], [309.2, 8.55]], [[309.2, 8.55], [-527.75, 82.5], [280.2, -14.45], [309.2, -14.45]], [[280.2, -14.45], [-527.75, 82.5], [254.2, -36.45], [280.2, -36.45]]]);
    const potolokBody = this.createBody("potolok", "Polygon", 0, 1, 0.2, [[[257, -78], [257, -19], [-200, 44], [-257, -10], [-257, -78]], [[-200, 44], [257, -19], [205, 33], [-200, 78]], [[205, 78], [-200, 78], [205, 33]]]);
    this.createBody("triangle", "Polygon", 0, 1, 0.2, [[[-375.95, -58], [-155.95, -36.75], [257.1, 31.15], [-373.9, 55]], [[404, 55], [-373.9, 55], [257.1, 31.15], [404, 31.2]], [[257.1, 31.15], [-155.95, -36.75], [112.1, -53.75]], [[112.1, -53.75], [-155.95, -36.75], [-0.4, -126.95]]]);
    this.movePlatform1Body = this.createBody("movePlatform1", "Polygon", 1, 1, 0.2, [[[75, -5], [75, 5], [-75, 5], [-75, -5]]]);
    this.movePlatform2Body = this.createBody("movePlatform2", "Polygon", 1, 1, 0.2, [[[75, -5], [75, 5], [-75, 5], [-75, -5]]]);
    this.jumpPlatform2Body = this.createBody("jumpPlatform2", "Polygon", 1, 1, 0.2, [[[100, 0], [100, 20], [0, 20], [0, 0]]]);
    this.createBody("vanna", "Polygon", 0, 1, 0.2, [[[415.5, -92.5], [415.5, -59.05], [194.55, 92.45], [164.55, 50.45], [234.55, -92.5]], [[-156.45, -89.5], [-63.45, 50.45], [-74.45, 92.45], [-415.45, -59.55], [-415.45, -89.5]], [[-63.45, 50.45], [164.55, 50.45], [194.55, 92.45], [-74.45, 92.45]]]);
    this.redWallBody = this.createBody("redWall", "Polygon", 0, 1, 0.2, [[[1.5, -61.5], [1.5, 61.5], [-18.5, 61.5], [-18.5, -61.5]]]);
    if (FlashLevelBaseRuntime.levelFlags[7].redCheckLevel) {
      this.destroyBody(this.redWallBody);
    }
    const bigMovePlatformBody = this.createBody("bigMovePlatform", "Polygon", 1, 1, 0.2, [[[-75, -5], [75, -5], [75, 5], [-75, 5]]]);
    this.createBody("endPlatform", "Polygon", 0, 1, 0.2, [[[150, 0], [150, 20], [0, 20], [0, 0]]]);
    for (let index = 1; index <= 18; index += 1) {
      this.createBody(`box${index}`, "Polygon", 0.3, 1, 0.2, [[[10, -10], [10, 10], [-10, 10], [-10, -10]]]);
    }
    this.bluePlatformBody = this.createBody("bluePlatform", "Polygon", 0, 1, 0.2, [[[140, 0], [140, 20], [0, 20], [0, 0]]]);
    this.starBody = this.createBody("star", "Polygon", 1, 1, 0.2, [[[-0.25, 15], [2, 27.85], [-2.15, 27.95]], [[2, 27.85], [15, 29.75], [2.1, 32], [-2.05, 32.1], [-15, 30.2], [-2.15, 27.95]], [[0.2, 45], [-2.05, 32.1], [2.1, 32]]]);
    this.starBody.SetAngularVelocity(-4);

    this.addDistanceJoint(bigMovePlatformBody, this.ground, new b2Vec2(3077 / 30, -190 / 30), new b2Vec2(3005 / 30, -305 / 30));
    this.addDistanceJoint(bigMovePlatformBody, this.ground, new b2Vec2(2944 / 30, -190 / 30), new b2Vec2(2873 / 30, -305 / 30));
    this.addDistanceJoint(this.starBody, potolokBody, new b2Vec2(619 / 30, -51.5 / 30), new b2Vec2(552 / 30, -111 / 30));
    this.jumpPlatform1Joint = this.addPrismaticJoint(this.jumpPlatform1Body, this.ground, this.jumpPlatform1Body.GetWorldCenter(), new b2Vec2(0, 1), {
      lowerTranslation: 0,
      upperTranslation: 1,
      enableLimit: true,
      maxMotorForce: 0,
      motorSpeed: 200,
      enableMotor: true,
    });
    this.jumpPlatform2Joint = this.addPrismaticJoint(this.jumpPlatform2Body, this.ground, this.jumpPlatform2Body.GetWorldCenter(), new b2Vec2(0, 1), {
      lowerTranslation: 0,
      upperTranslation: 1,
      enableLimit: true,
      maxMotorForce: 0,
      motorSpeed: 200,
      enableMotor: true,
    });
    this.addPrismaticJoint(this.movePlatform1Body, this.ground, this.movePlatform1Body.GetWorldCenter(), new b2Vec2(0, 1), { enableLimit: false, enableMotor: false });
    this.addPrismaticJoint(this.movePlatform2Body, this.ground, this.movePlatform2Body.GetWorldCenter(), new b2Vec2(1, 0), { enableLimit: false, enableMotor: false });
    this.movePlatform1BodyDirection = 1;
    this.movePlatform2BodyDirection = 1;
  }

  levelUpdate() {
    if (this.player.y > 500 || this.contactListener.playerContactBodies.indexOf(this.starBody) !== -1) {
      if (this.player.isLive()) {
        this.startDeath();
      }
    }
    this.jumpPlatform1Joint.SetMaxMotorForce(this.contactListener.playerContactBodies.indexOf(this.jumpPlatform1Body) !== -1 ? 150 : 0);
    this.jumpPlatform2Joint.SetMaxMotorForce(this.contactListener.playerContactBodies.indexOf(this.jumpPlatform2Body) !== -1 ? 170 : 0);
    const movePlatform1 = this.getDisplayLayout("movePlatform1");
    if (movePlatform1.y < -30) {
      this.movePlatform1BodyDirection = 1;
    }
    if (movePlatform1.y > 115) {
      this.movePlatform1BodyDirection = -1;
    }
    this.movePlatform1Body.SetLinearVelocity(new b2Vec2(0, 3 * this.movePlatform1BodyDirection));
    const movePlatform2 = this.getDisplayLayout("movePlatform2");
    if (movePlatform2.x < 1495) {
      this.movePlatform2BodyDirection = 1;
    }
    if (movePlatform2.x > 1645) {
      this.movePlatform2BodyDirection = -1;
    }
    this.movePlatform2Body.SetLinearVelocity(new b2Vec2(3 * this.movePlatform2BodyDirection, 0));
    if (this.hitNamedObject("redCheck") && this.getNamedObjectFrame("redCheck") === 1 && this.redWallBody) {
      this.setNamedObjectFrame("redCheck", 2);
      this.destroyBody(this.redWallBody);
      this.redWallBody = null;
      FlashLevelBaseRuntime.levelFlags[7].redCheckLevel = true;
    }
    if (this.hitNamedObject("blueCheck") && this.getNamedObjectFrame("blueCheck") === 1 && this.bluePlatformBody) {
      this.setNamedObjectFrame("blueCheck", 2);
      this.destroyBody(this.bluePlatformBody);
      this.bluePlatformBody = null;
    }
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    if (this.backgroundImage?.complete && this.backgroundStart && this.backgroundSymbol?.layout) {
      const sym = this.backgroundSymbol;
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      ctx.save();
      ctx.translate(bx, by);
      if (sym.pngLayout) {
        const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
        const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
        ctx.drawImage(
          this.backgroundImage,
          sym.pngLayout.cropX, sym.pngLayout.cropY,
          sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
          sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
          sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
          sym.pngLayout.cropWidth * scaleX,
          sym.pngLayout.cropHeight * scaleY,
        );
      } else {
        ctx.drawImage(this.backgroundImage, sym.layout.imageOffsetX, sym.layout.imageOffsetY);
      }
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    if (this.stageImage?.complete) {
      const { layout } = this.levelData;
      ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY, layout.imageWidth, layout.imageHeight);
    }

    for (const name of LEVEL7_DYNAMIC_DRAW_ORDER) {
      const layout = this.getDisplayLayout(name);
      if (!layout) {
        continue;
      }
      drawSymbolImage(
        ctx,
        this.level7DynamicImages[name],
        LEVEL7_DYNAMIC_SYMBOLS[name],
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }

    for (let index = 1; index <= 18; index += 1) {
      const name = `box${index}`;
      const layout = this.getDisplayLayout(name);
      if (!layout) {
        continue;
      }
      drawSymbolImage(ctx, this.boxImage, BOX_SYMBOL, layout.x, layout.y, layout.rotation ?? 0);
    }

    for (const checkpointState of this.checkpoints) {
      const checkpointFrame = this.checkpointFrames[checkpointState.currentFrame - 1];
      drawAnimatedFrame(ctx, checkpointFrame, CHECKPOINT_SYMBOL, checkpointState.x, checkpointState.y);
    }

    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    drawAnimatedFrame(ctx, goalFrame, GOAL_SYMBOL, this.goalPosition.x, this.goalPosition.y);

    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
